import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withTranslation, TFunction } from 'react-i18next';
import './UploadFileComponent.css';
import UploadFileActions from '../../redux/UploadFileRedux';
import UploadMakeActions from '../../redux/UploadMakeRedux';
import DeleteFileActions from '../../redux/DeleteFileRedux';
import DeleteMakeActions from '../../redux/DeleteMakeRedux';
import GetFileActions from '../../redux/GetFileRedux';
import GetWithdrawalActions from '../../redux/GetWithdrawalRedux';
import AlertBox from '../AlertBox';
import ConfirmBox from '../ConfirmBox';

interface UploadFileComponentProps {
  t?: TFunction;
  leave_id?: string;
  children_id?: string;
  withdrawal_id?: string;
  isSickImage?: boolean;
  is_submit_missing_note?: boolean;
  missing?: boolean;
  onFileUpload?: (count: number) => void;
  
  // Redux Props
  uploadFileFetching?: boolean;
  uploadFileSuccess?: boolean;
  uploadFileError?: boolean;
  uploadMakeFetching?: boolean;
  uploadMakeSuccess?: boolean;
  uploadMakeError?: boolean;
  listFile?: any;
  listFileWithdrawal?: any;
  Status?: any;
  StatusMake?: any;
  
  // Redux Actions
  uploadFile?: (leave_id: string, children_id: string, files: FormData) => void;
  uploadMake?: (withdrawal_id: string, withdrawal_file: FormData) => void;
  deleteFile?: (leave_id: string, children_id: string, file_id: string) => void;
  deleteMake?: (withdrawal_id: string, file_id: string) => void;
  getFile?: (leave_id: string) => void;
  getWithdrawal?: (withdrawal_id: string) => void;
  clearUploadFileData?: () => void;
  clearDeleteFileData?: () => void;
  clearGetFileData?: () => void;
  clearGetWithdrawalData?: () => void;
}

interface UploadFileComponentState {
  fileUpload: any[];
  dataFormData: FormData | null;
  alertMessage: string;
  alertPopupOpen: boolean;
  confirmPopupOpen: boolean;
  confirmSubmitPopupOpen: boolean;
  navigateTo: string;
  fileId: string;
}

class UploadFileComponent extends Component<UploadFileComponentProps, UploadFileComponentState> {
  private fileInputRef = React.createRef<HTMLInputElement>();

  constructor(props: UploadFileComponentProps) {
    super(props);
    
    // Clear cross-contamination: Clear withdrawal data if we're on a leave-only page (no withdrawal_id)
    if (this.props.leave_id && !this.props.withdrawal_id && this.props.clearGetWithdrawalData) {
      this.props.clearGetWithdrawalData();
    }
    
    // Clear cross-contamination: Clear leave data if we're on a withdrawal page
    // (withdrawal_id takes priority, even if leave_id is also present)
    if (this.props.withdrawal_id && this.props.clearGetFileData) {
      this.props.clearGetFileData();
    }
    
    // Initialize data fetching - match reference
    // Only fetch leave files if we're on a leave page (not withdrawal page)
    // withdrawal_id takes priority - if it exists, don't fetch leave files
    if (this.props.leave_id && this.props.getFile && !this.props.is_submit_missing_note && !this.props.withdrawal_id) {
      this.props.getFile(this.props.leave_id);
    }
    if (this.props.withdrawal_id && this.props.getWithdrawal) {
      this.props.getWithdrawal(this.props.withdrawal_id);
    }
    
    this.state = {
      fileUpload: [],
      dataFormData: null,
      alertMessage: '',
      alertPopupOpen: false,
      confirmPopupOpen: false,
      fileId: '',
      confirmSubmitPopupOpen: false,
      navigateTo: '',
    };
  }

  componentDidUpdate(prevProps: UploadFileComponentProps) {
    // Handle upload success - match reference
    if (
      (prevProps.uploadFileSuccess !== this.props.uploadFileSuccess && this.props.uploadFileSuccess) ||
      (prevProps.uploadMakeSuccess !== this.props.uploadMakeSuccess && this.props.uploadMakeSuccess)
    ) {
      this.uploadFileSuccess();
    }

    // Handle upload errors - match reference  
    if (
      (this.props.uploadFileError && this.props.uploadFileError !== prevProps.uploadFileError) ||
      (this.props.uploadMakeError && this.props.uploadMakeError !== prevProps.uploadMakeError)
    ) {
      const errorMessage = this.props.t ? 
        this.props.t('select-leavere-quest:upload-false') : 
        'Upload failed';
      alert(errorMessage);
    }
  }

  componentWillUnmount() {
    // Clear Redux data for upload and delete actions
    if (this.props.clearUploadFileData) {
      this.props.clearUploadFileData();
    }
    if (this.props.clearDeleteFileData) {
      this.props.clearDeleteFileData();
    }
    
    // Clear file list data to prevent cross-contamination between pages
    // Clear withdrawal data if this was a withdrawal context
    if (this.props.withdrawal_id && this.props.clearGetWithdrawalData) {
      this.props.clearGetWithdrawalData();
    }
    
    // Clear leave file data if this was a leave context (but not for missing note submission)
    if (this.props.leave_id && !this.props.is_submit_missing_note && this.props.clearGetFileData) {
      this.props.clearGetFileData();
    }
  }

  uploadFileSuccess = () => {
    setTimeout(() => {
      if (this.props.withdrawal_id) {
        this._messageWithdrawal();
      } else {
        this._message();
      }
    }, 500);
  };

  _messageWithdrawal = () => {
    if (this.props.StatusMake?.success === true) {
      const successMessage = this.props.t ? 
        this.props.t('withdrawal:upload-successfully') : 
        'Upload successfully';
      alert(successMessage);
      if (this.props.withdrawal_id && this.props.getWithdrawal) {
        this.props.getWithdrawal(this.props.withdrawal_id);
      }
    } else if (this.props.StatusMake?.success === false) {
      alert(this.props.StatusMake?.message || 'Upload failed');
    }
  };

  _message = () => {
    if (this.props.Status?.success === true) {
      const successMessage = this.props.t ? 
        this.props.t('withdrawal:upload-successfully') : 
        'Upload successfully';
      this.setState({ alertMessage: successMessage, alertPopupOpen: true });
      
      // Clear fileUpload state after successful upload
      this.setState({ fileUpload: [] }, () => {
        // Notify parent component of file count change
        if (this.props.onFileUpload) {
          this.props.onFileUpload(0);
        }
      });
      
      if (this.props.leave_id && this.props.getFile) {
        this.props.getFile(this.props.leave_id);
      }
    } else if (this.props.Status?.success === false) {
      this.setState({ alertMessage: this.props.Status?.message || 'Upload failed', alertPopupOpen: true });
    }
  };

  _onUploadPhoto = () => {
    if (this.fileInputRef.current) {
      this.fileInputRef.current.click();
    }
  };

  _onFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (10MB limit - match reference)
    if (file.size > 10 * 1024 * 1024) {
      // alert('File size must be less than 10MB');
      this.setState({
        alertMessage: this.props.t ? this.props.t('file-upload:file-size-error') : 'File size must be less than 10MB',
        alertPopupOpen: true,
      });
      return;
    }

    // Validate file type - allow common document and image types
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif',
      'application/pdf', 'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      // alert('File type not supported. Please select a valid document or image file.');
      this.setState({
        alertMessage: this.props.t ? this.props.t('file-upload:file-type-error') : 'File type not supported. Please select a valid document or image file.',
        alertPopupOpen: true,
      });
      return;
    }

    // Check upload limit (5 files - match reference)
    if (this.state.fileUpload.length >= 5) {
      // alert('Maximum 5 files allowed');
      this.setState({
        alertMessage: this.props.t ? this.props.t('file-upload:file-limit-error') : 'Maximum 5 files allowed',
        alertPopupOpen: true,
      });
      return;
    }

    const fileObj = {
      file: file,
      name: file.name,
      type: file.type,
    };

    this.setState({
      fileUpload: [...this.state.fileUpload, fileObj]
    }, () => {
      // Notify parent component of file count change
      if (this.props.onFileUpload) {
        this.props.onFileUpload(this.state.fileUpload.length);
      }
    });

    // Create FormData and upload immediately - match reference behavior
    const data = new FormData();
    
    if (this.props.withdrawal_id && this.props.withdrawal_id !== 'withdrawal_form') {
      // For withdrawal - match reference
      data.append('withdrawal_file', file);
      
      if (this.props.uploadMake && this.props.withdrawal_id) {
        console.log('UploadFileComponent: Uploading withdrawal file with ID:', this.props.withdrawal_id);
        console.log('UploadFileComponent: File being uploaded:', file);
        // Pass the FormData - uploadMake now handles both File and FormData inputs
        this.props.uploadMake(this.props.withdrawal_id, data);
      }
    } else if (this.props.withdrawal_id === 'withdrawal_form') {
      console.warn('UploadFileComponent: Cannot upload file - invalid withdrawal ID:', this.props.withdrawal_id);
      alert('Cannot upload file. Please create a withdrawal first.');
      return;
    } else if (this.props.leave_id && this.props.children_id) {
      // For leave - match reference project structure
      console.log('UploadFileComponent: Uploading leave file with ID:', this.props.leave_id);
      console.log('UploadFileComponent: File being uploaded:', file);
      
      // Create FormData like reference project (indexed array format)
      const formData = new FormData();
      
      // Add all files from fileUpload state with indexed format (files[0], files[1], etc.)
      this.state.fileUpload.forEach((fileObj, index) => {
        formData.append(`files[${index}]`, fileObj.file);
      });
      
      // Add the new file with correct index
      formData.append(`files[${this.state.fileUpload.length}]`, file);
      
      formData.append('is_submit_missing_note', this.props.is_submit_missing_note ? '1' : '0');
      
      // Store FormData in state (like reference project)
      this.setState({ dataFormData: formData });
      
      // Debug: Log FormData contents
      console.log('UploadFileComponent: FormData contents:');
      Array.from(formData.entries()).forEach(([key, value]) => {
        console.log(`${key}:`, value);
      });
      
      // Debug: Log parameters
      console.log('UploadFileComponent: Parameters:', {
        leave_id: this.props.leave_id,
        children_id: this.props.children_id,
        is_submit_missing_note: this.props.is_submit_missing_note,
        file: {
          name: file.name,
          type: file.type,
          size: file.size
        }
      });
      
      if (this.props.uploadFile && this.props.leave_id && this.props.children_id) {

        if ( !this.props.is_submit_missing_note ){
          // Pass FormData directly like reference project
          this.props.uploadFile(this.props.leave_id, this.props.children_id, formData);
        }
      }
    }
  };

  _onDeletePress = (key: number, id: string) => {
    const confirmMessage = this.props.t ? 
      this.props.t('galleryDeleteComponent:alert.message') : 
      'Are you sure you want to delete this file?';

    this.setState({
      confirmPopupOpen: true,
      fileId: id,
      alertMessage: confirmMessage,
    });
    
    /* if (window.confirm(confirmMessage)) {
      this._onDeleteFile(id);
    } */
  };

  _onDeleteConfirm = () => {
    this.setState({
      confirmPopupOpen: false,
      alertMessage: '',
    });
    this._onDeleteFile(this.state.fileId);
  };

  _onDeleteFile = (id: string) => {
    if (this.props.leave_id && this.props.children_id && this.props.deleteFile) {
      this.props.deleteFile(this.props.leave_id, this.props.children_id, id);
    } else if (this.props.withdrawal_id && this.props.deleteMake) {
      this.props.deleteMake(this.props.withdrawal_id, id);
    }

    // Refresh data after delete
    setTimeout(() => {
      if (this.props.leave_id && this.props.getFile) {
        this.props.getFile(this.props.leave_id);
      } else if (this.props.withdrawal_id && this.props.getWithdrawal) {
        this.props.getWithdrawal(this.props.withdrawal_id);
      }
    }, 500);
  };

  deleteLocalFile = (fileName: string) => {
    const confirmMessage = this.props.t ? 
      this.props.t('galleryDeleteComponent:alert.message') : 
      'Are you sure you want to delete this file?';
    
    if (window.confirm(confirmMessage)) {
      const result = this.state.fileUpload.filter(item => item.name !== fileName);
      this.setState({ fileUpload: result }, () => {
        // Notify parent component of file count change
        if (this.props.onFileUpload) {
          this.props.onFileUpload(this.state.fileUpload.length);
        }
      });
    }
  };

  // Submit files method - match reference project exactly
  uploadFiles = () => {
    const confirmMessage = this.props.t ? 
      this.props.t('file-upload:confirm-submit') : 
      'If you submit, you are about to submit all the file and can not edit them later';

    this.setState({
      confirmSubmitPopupOpen: true,
      alertMessage: confirmMessage,
    });
    /*
    
    if (window.confirm(confirmMessage)) {
      // Use stored FormData from state (like reference project)
      if (this.state.dataFormData && this.props.uploadFile && this.props.leave_id && this.props.children_id) {
        console.log('UploadFileComponent: Submitting stored FormData:', {
          fileCount: this.state.fileUpload.length,
          leave_id: this.props.leave_id,
          children_id: this.props.children_id
        });
        
        this.props.uploadFile(this.props.leave_id, this.props.children_id, this.state.dataFormData);
      } else {
        console.error('UploadFileComponent: No FormData available for submission');
        alert('No files to submit');
      }
    }*/
  };

  _onSubmitConfirm = () => {
    this.setState({
      confirmSubmitPopupOpen: false,
      alertMessage: '',
      navigateTo: 'calendar',
    });

    if (this.state.dataFormData && this.props.uploadFile && this.props.leave_id && this.props.children_id) {
      console.log('UploadFileComponent: Submitting stored FormData:', {
        fileCount: this.state.fileUpload.length,
        leave_id: this.props.leave_id,
        children_id: this.props.children_id
      });
      
      this.props.uploadFile(this.props.leave_id, this.props.children_id, this.state.dataFormData);
    } else {
      console.error('UploadFileComponent: No FormData available for submission');
      alert('No files to submit');
    }

  };

  render() {
    let albums = null;

    // Server files for regular leave (only show if we're on a leave page, not withdrawal)
    // Context check: must have leave_id and NOT have withdrawal_id to prevent cross-contamination
    if (this.props.listFile?.data?.length && this.props.leave_id && !this.props.withdrawal_id) {
      albums = (
        <div className="fileupload-error fileupload-list">
          {this.props.listFile.data.map((item: any, key: number) => (
            <div key={key}>
                <span>SICK NOTE {key + 1}</span>
                <button 
                  onClick={() => this._onDeletePress(key, item.id)}
                >
                  ✕
                </button>
            </div>
          ))}
        </div>
      );
    }
    // Server files for withdrawal (only show if we're on a withdrawal page)
    // Context check: withdrawal_id takes priority - show withdrawal files if withdrawal_id exists
    else if (this.props.listFileWithdrawal?.data?.length && this.props.withdrawal_id) {
      albums = (
        <div className="fileupload-error withdrawal-file-list">
          {this.props.listFileWithdrawal.data.map((item: any, key: number) => (
            <div key={key}>
                  <span>
                    Withdrawal form {key + 1}
                  </span>
                <button 
                  onClick={() => this._onDeletePress(key, item.id)}
                >
                  ✕
                </button>
            </div>
          ))}
        </div>
      );
    }
    // Show local uploaded files for missing note submission (only when no server files)
    else if (this.state.fileUpload.length > 0 && this.props.is_submit_missing_note) {
      albums = (
        <div className="fileupload-error missing-note-file-list">
          {this.state.fileUpload.map((item: any, key: number) => (
            <div key={key}>
                  <span>
                    {this.props.withdrawal_id ? 'Withdrawal form' : 
                     this.props.isSickImage ? 'SICK NOTE' : 'Withdrawal form'} {key + 1}
                  </span>
                <button 
                  onClick={() => this.deleteLocalFile(item.name)}
                >
                  ✕
                </button>
            </div>
          ))}
        </div>
      );
    }

    return (
      <>
        {/* Loading indicator */}
        {/* {(this.props.uploadFileFetching || this.props.uploadMakeFetching) && (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
            <div>Uploading...</div>
          </div>
        )} */}

        {/* Upload button section */}
        <div className="upload-btns">
          <button
            onClick={this._onUploadPhoto}
          >
            <span>
              {this.props.t ? this.props.t('withdrawal:upload-file') : 'Upload file'}
            </span>
          </button>

          <p>
            {this.props.withdrawal_id ? (
              // For withdrawal forms - match reference
              this.props.t ? this.props.t('withdrawal:button-upload') : '(Limit 3 files. 10MB per file upload allow)'
            ) : this.props.missing ? (
              // For missing notes
              this.props.t ? this.props.t('withdrawal:limit-files-missing') : '(Limit 5 files. 10MB per file upload allow)'
            ) : (
              // For regular leave requests (not withdrawal)
              this.props.t ? this.props.t('withdrawal:limit-files') : '(Limit 5 files. 10MB per file upload allow. Sick note file is not required and can be uploaded later)'
            )}
          </p>
        </div>

        {/* Hidden file input */}
        <input
          type="file"
          ref={this.fileInputRef}
          style={{ display: 'none' }}
          onChange={this._onFileSelect}
          accept="image/*,.pdf,.doc,.docx"
          multiple={false}
        />

        {/* File list */}
        {albums}

        {/* Submit button for missing note files */}
        { this.state.fileUpload.length > 0 && this.props.is_submit_missing_note && (
          <div className="submit-button-container">
            <button
              className="submit-button comman-btn w-100"
              onClick={this.uploadFiles}
              disabled={this.props.uploadFileFetching}
            >
              <span>
                {this.props.uploadFileFetching ? 
                  (this.props.t ? this.props.t('common:loading') : 'Loading...') :
                  (this.props.t ? this.props.t('make-up-session:submit') : 'Submit')
                }
              </span>
            </button>
          </div>
        )}

        <AlertBox 
          title={this.props.t ? this.props.t('common:message') : 'Message'}
          message={this.state.alertMessage} 
          isVisible={this.state.alertPopupOpen}
          onClose={() => {}}
          okAction={() => {
            this.setState({ alertPopupOpen: false });
            if (this.state.navigateTo == 'calendar' && typeof (this.props as any).navigate === 'function') {
              (this.props as any).navigate('/calendar');
            }
          }}
          okText={this.props.t ? this.props.t('common:ok') : 'OK'}
        />

        <ConfirmBox
          message={this.state.alertMessage} 
          title={this.props.t ? this.props.t('common:confirm') : 'Confirm'} 
          confirmAction={this._onDeleteConfirm}
          cancelAction={() => {
            this.setState({ confirmPopupOpen: false });
          }} 
          isVisible={this.state.confirmPopupOpen} 
          onClose={() => {}} 
          confirmText={this.props.t ? this.props.t('common:yes') : 'Yes'} 
          cancelText={this.props.t ? this.props.t('common:cancel') : 'Cancel'} 
        />

        <ConfirmBox
          message={this.state.alertMessage} 
          title={this.props.t ? this.props.t('common:confirm') : 'Confirm'} 
          confirmAction={this._onSubmitConfirm}
          cancelAction={() => {
            this.setState({ confirmSubmitPopupOpen: false });
          }} 
          isVisible={this.state.confirmSubmitPopupOpen} 
          onClose={() => {}} 
          confirmText={this.props.t ? this.props.t('common:yes') : 'Yes'} 
          cancelText={this.props.t ? this.props.t('common:cancel') : 'Cancel'} 
        />

      </>
    );
  }
}

// Match reference Redux connection
const mapStateToProps = (state: any) => ({
  uploadFileError: state.uploadFile.error,
  uploadFileSuccess: state.uploadFile.success,
  uploadFileFetching: state.uploadFile.fetching,
  listFile: state.getFile.listFile,
  Status: state.uploadFile.Status,
  listFileWithdrawal: state.getWithdrawal.listFileWithdrawal,
  uploadMakeError: state.uploadMake.error,
  uploadMakeSuccess: state.uploadMake.success,
  uploadMakeFetching: state.uploadMake.fetching,
  StatusMake: state.uploadMake.Status,
});

const mapDispatchToProps = (dispatch: any) => ({
  uploadFile: (leave_id: string, children_id: string, files: FormData) =>
    dispatch(UploadFileActions.uploadFileRequest(leave_id, children_id, files)),
  uploadMake: (withdrawal_id: string, withdrawal_file: FormData) =>
    dispatch(UploadMakeActions.uploadMakeRequest(withdrawal_id, withdrawal_file)),
  deleteFile: (leave_id: string, children_id: string, file_id: string) =>
    dispatch(DeleteFileActions.deleteFileRequest(leave_id, children_id, file_id)),
  deleteMake: (withdrawal_id: string, file_id: string) =>
    dispatch(DeleteMakeActions.deleteMakeRequest(withdrawal_id, file_id)),
  getFile: (leave_id: string) => dispatch(GetFileActions.getFileRequest(leave_id)),
  getWithdrawal: (withdrawal_id: string) =>
    dispatch(GetWithdrawalActions.getWithdrawalRequest(withdrawal_id)),
  clearUploadFileData: () => dispatch(UploadFileActions.clearUploadFileData()),
  clearDeleteFileData: () => dispatch(DeleteFileActions.clearDeleteFileData()),
  clearGetFileData: () => dispatch(GetFileActions.clearGetFileData()),
  clearGetWithdrawalData: () => dispatch(GetWithdrawalActions.clearGetWithdrawalData()),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(UploadFileComponent));
